import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { compareSpecs, compareTally } from '../../domain/compare/carComparison';
import { formatSpecNumber } from '../../domain/cars/carProperties';

/**
 * Every figure, twice (SPEC-008 PR-8.5).
 *
 * A real `<table>` with two value columns, for the reason `CarSpecTable` is one:
 * a row is a label and its figures, and three loose strings in a flex row is
 * three unrelated things to anybody reading by ear. Here it matters more, not
 * less — the whole point of the band is that two cells belong to the same row,
 * and a screen reader announcing the column header with each cell is the only
 * way that survives without sight.
 *
 * WHICH CAR LEADS IS SAID IN THE MARKUP, not only in the tint. The leading cell
 * carries a visually hidden word, so a reader who cannot see the heat wash is
 * told the same thing sighted readers are — the garage's pills make the same
 * argument for `aria-pressed`, and it is the house rule rather than a decision
 * taken again here.
 *
 * The domain hands over message KEYS and formatted values; every word on this
 * table is resolved in this component. That is what keeps `carComparison.js`
 * free of any knowledge of the language on screen.
 */

function LeaderMark({ isLeader }) {
    const t = useTranslate();

    return isLeader ? <span className="visually-hidden">{t('compare.leads')}</span> : null;
}

function ValueCell({ isLeader, side }) {
    const t = useTranslate();
    const classes = [
        'compare-table__value',
        // Tabular figures are for figures. "Pirelli P Zero" set in the numeric
        // face beside 324 and 478 reads as a part number.
        side.numeric === false ? 'compare-table__value--words' : '',
        isLeader ? 'compare-table__value--leads' : '',
    ].filter(Boolean).join(' ');

    return (
        <td className={classes}>
            <LeaderMark isLeader={isLeader} />
            <span className="compare-table__figure">
                {side.valueKey ? t(side.valueKey, side.valueValues) : side.value}
            </span>
            {side.unitKey ? <span className="compare-table__unit">{t(side.unitKey)}</span> : null}
        </td>
    );
}

export default function CompareSpecTable({ cars }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const rows = compareSpecs(cars, { languageCode });
    const tally = compareTally(rows);

    if (rows.length === 0) {
        return null;
    }

    return (
        <div className="compare-table">
            <table>
                <caption className="visually-hidden">
                    {t('compare.table.caption', { a: carName(cars[0]), b: carName(cars[1]) })}
                </caption>

                <thead>
                    <tr>
                        <th scope="col">{t('compare.table.figure')}</th>
                        {cars.map((car, index) => (
                            <th scope="col" key={car.id + String(index)}>
                                <span className="compare-table__head-name">{carName(car)}</span>
                                {/* A count rather than a verdict: "leads on
                                    seven figures" is arithmetic the reader can
                                    check by scrolling, where "the better car" is
                                    an opinion the catalogue has not earned. */}
                                <span className="compare-table__head-tally">
                                    {t('compare.table.leadCount', {
                                        count: formatSpecNumber(tally[index], { languageCode }),
                                    })}
                                </span>
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key}>
                            {/* The label comes from the FIRST side, because a
                                dated money label differs per car — the F40's
                                list price is a 1987 price and the Supra's is a
                                1993 one — and the row can only be named once.
                                The years themselves are in the cells. */}
                            <th scope="row">{t(row.sides[0].labelKey, row.sides[0].labelValues)}</th>
                            {row.sides.map((side, index) => (
                                <ValueCell
                                    key={row.key + String(index)}
                                    side={side}
                                    isLeader={row.leader === index}
                                />
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
